/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  Asset,
  ParentalRatingOptionsType,
  TimeUtils,
  ParentalRatingValue,
  Time,
  ClientRatingWindow,
  ParentalControlSettings,
  ParentalControlUtils,
  Ducks,
  AssetUtils,
} from '@wit/vtv-x-foundation-lib';
import store from 'store/store';
import { Selectors as ParentalControlSelectors } from '../../store/ducks/parentalControl';
import { Selectors as ChannelsSelectors } from '../../store/ducks/epg/channels';

export enum ActiveRatingLockingLogic {
  LOCK = 'lock',
  UNLOCK = 'unlock',
}

export default class ParentalControl extends ParentalControlUtils {
  // Note: update this value regarding the UI
  // for the web it is 'lock' logic, so the selected rating is the first locked rating
  static activeRatingLockingLogic: ActiveRatingLockingLogic = ActiveRatingLockingLogic.LOCK;

  static UI = {
    isAdult: ParentalControl.isAdult,
    getAssetParentalState: ParentalControl.getAssetParentalState,
    getAssetParentalRating: ParentalControl.getAssetRating,
    isAssetInLockedChannel: ParentalControl.isAssetInLockedChannel,
    getActiveRating: ParentalControl.getActiveRating,
  };

  /* ************************************************
   * UI Methods
   ************************************************ */

  protected static isAdult(asset: Asset): boolean {
    const settings = ParentalControl.getParentalSettings();
    return super.isAdult(asset, settings);
  }

  private static getAssetParentalState(asset: Asset): ParentalRatingOptionsType {
    // Check the user is in grace period
    if (ParentalControl.isInGracePeriod()) {
      return ParentalRatingOptionsType.SHOW;
    }

    // 1. Check the parental is currently locked
    if (!ParentalControl.isParentalLocked()) {
      return ParentalRatingOptionsType.SHOW;
    }

    // 2. Check the channel is currently locked
    if (ParentalControl.isChannelLocked(asset)) {
      return ParentalRatingOptionsType.PIN;
    }

    // 3. Check the asset is currently locked
    if (!ParentalControl.isAssetLocked(asset)) {
      return ParentalRatingOptionsType.SHOW;
    }

    // Get the active Rating option type
    const activeRating = ParentalControl.getActiveRating();
    if (activeRating?.options.default) {
      return activeRating.options.default;
    }

    // If no options are available PIN is assumed (CI112-RUI0039.3)
    return ParentalRatingOptionsType.PIN;
  }

  private static isAssetInLockedChannel(asset: Asset): boolean {
    // Check the user is in grace period
    if (ParentalControl.isInGracePeriod()) {
      return false;
    }

    // 1. Check the parental control is enable
    if (!ParentalControl.isParentalLocked()) {
      return false;
    }

    // Check the associated channel is currently locked
    if (!ParentalControl.isChannelLocked(asset)) {
      return false;
    }
    return true;
  }

  /* ************************************************
   * Internal Methods
   ************************************************ */

  private static isInGracePeriod(): boolean {
    const dmsParentalControl = ParentalControl.getParentalSettings();
    const parentalLockOccurred = ParentalControl.getParentalLockOccurred();

    if (dmsParentalControl?.parentalLockTimeoutMinutes && parentalLockOccurred) {
      const timeToUnlock = TimeUtils.addMinutes(parentalLockOccurred, dmsParentalControl.parentalLockTimeoutMinutes);

      return TimeUtils.isAfterNow(timeToUnlock);
    }

    return false;
  }

  private static isParentalLocked(): boolean {
    const isParentalEnable = ParentalControl.isParentalValidationEnable();
    const clientWindow = ParentalControl.getClientRatingWindowFromTime(TimeUtils.now());

    // If client window exists at the current time, the parental control is locked,
    // even if parental control is disabled in the settings
    if (clientWindow) {
      return true;
    }

    if (isParentalEnable !== undefined && !isParentalEnable) {
      return false;
    }

    return true;
  }

  private static isAssetLocked(asset: Asset): boolean {
    const settings = ParentalControl.getParentalSettings();
    if (settings) {
      const assetRating = ParentalControl.getAssetRating(asset, settings);
      const activeRating = ParentalControl.getActiveRating();

      if (assetRating !== undefined && activeRating !== undefined) {
        return (
          assetRating > activeRating.ratingValue ||
          (ParentalControl.activeRatingLockingLogic === ActiveRatingLockingLogic.LOCK &&
            assetRating === activeRating.ratingValue)
        );
      }
    }
    // parental control is off
    return false;
  }

  private static isChannelLocked(asset: Asset): boolean {
    const storeState = store().getState();
    const lockedChannels = ParentalControlSelectors.getLockedChannels(storeState);
    if (AssetUtils.isChannel(asset)) {
      return lockedChannels.includes(asset.channelID);
    }
    if (AssetUtils.isTvAsset(asset)) {
      const channelInfo = ChannelsSelectors.getChannelByEpgId(storeState, asset.channelEpgId);
      if (channelInfo?.channelID) {
        return lockedChannels.includes(channelInfo?.channelID);
      }
      return false;
    }
    return false;
  }

  protected static getAssetRating(
    asset: Asset,
    settings?: ParentalControlSettings,
    skipWatershed = false
  ): number | undefined {
    const parentalSettings = settings || ParentalControl.getParentalSettings();
    if (parentalSettings) {
      return super.getAssetRating(asset, parentalSettings, skipWatershed);
    }

    return undefined;
  }

  private static getActiveRating(): ParentalRatingValue | undefined {
    let parentalRatingFromWindow: ParentalRatingValue | undefined;
    let parentalRatingFromSetting: ParentalRatingValue | undefined;
    const parentalRatingValues: ParentalRatingValue[] | undefined =
      ParentalControl.getParentalSettings()?.parentalRating.parentalRatingValues;

    const windowClientRating = ParentalControl.getClientRatingWindowFromTime(TimeUtils.now());
    if (windowClientRating) {
      parentalRatingFromWindow = parentalRatingValues?.find(
        (parentalRating) => parentalRating.ratingValue === windowClientRating.ratingValue
      );
    }
    const isParentalEnable = ParentalControl.isParentalValidationEnable();
    if (isParentalEnable) {
      parentalRatingFromSetting = ParentalControlSelectors.getAgeRating(store().getState());

      if (parentalRatingFromWindow && parentalRatingFromSetting) {
        return parentalRatingFromWindow.ratingValue < parentalRatingFromSetting.ratingValue
          ? parentalRatingFromWindow
          : parentalRatingFromSetting;
      }
    }

    return parentalRatingFromWindow || parentalRatingFromSetting;
  }

  private static getClientRatingWindowFromTime(time: Time): ClientRatingWindow | undefined {
    let window: ClientRatingWindow | undefined;
    const clientRatingWindows: ClientRatingWindow[] | undefined =
      ParentalControl.getParentalSettings()?.clientRatingWindow;
    const parentalRatingValues: ParentalRatingValue[] | undefined =
      ParentalControl.getParentalSettings()?.parentalRating.parentalRatingValues;

    // CI112 -RUI0200
    // Rating value of the object shall match the one of the ParentalRatingScheme defined
    // Only during  the time window defined (startTime and endTime), the client shall consider the rating value set.
    const availableWindows = clientRatingWindows?.filter((ratingWindow) => {
      let isInsideCW = false;

      try {
        const timeStartWs: Time | undefined = TimeUtils.setTimeFromFormatIntoDate(
          ratingWindow.startTime,
          TimeUtils.FORMAT.WATERSHED,
          time
        );
        const timeEndWs: Time | undefined = TimeUtils.setTimeFromFormatIntoDate(
          ratingWindow.endTime,
          TimeUtils.FORMAT.WATERSHED,
          time
        );

        if (timeStartWs && timeEndWs) {
          if (TimeUtils.isAfter(timeStartWs, timeEndWs)) {
            const firstWsWindow = {
              start: TimeUtils.subDays(timeStartWs, 1),
              end: timeEndWs,
            };
            const secondWsWindow = {
              start: timeStartWs,
              end: TimeUtils.addDays(timeEndWs, 1),
            };

            if (TimeUtils.isBetween(firstWsWindow.start, firstWsWindow.end, time)) {
              isInsideCW = true;
            }
            if (TimeUtils.isBetween(secondWsWindow.start, secondWsWindow.end, time)) {
              isInsideCW = true;
            }
          } else if (TimeUtils.isBetween(timeStartWs, timeEndWs, time)) {
            isInsideCW = true;
          }
        }

        return (
          isInsideCW &&
          parentalRatingValues?.findIndex((ratingValues) => ratingValues.ratingValue === ratingWindow.ratingValue) !==
            -1
        );
      } catch (error) {
        return false;
      }
    });

    // if two or more time windows overlap, the most restrictive applies
    if (availableWindows) {
      for (let i = 0; i < availableWindows.length; i += 1) {
        if ((window && availableWindows[i].ratingValue < window.ratingValue) || !window) {
          window = availableWindows[i];
        }
      }
    }

    return window;
  }

  private static isParentalValidationEnable(): boolean | undefined {
    return Ducks.Household.Selectors.data.getDefaultUser(store().getState())?.parentalValidation;
  }

  private static getParentalSettings(): ParentalControlSettings | undefined {
    return Ducks.Dms.Selectors.getParentalControl(store().getState());
  }

  private static getParentalLockOccurred(): Time | undefined {
    return ParentalControlSelectors.getParentalLockOccurred(store().getState());
  }
}
